import {
  Video, Image as ImageIcon, Clapperboard, SlidersHorizontal, CalendarDays, FolderKanban,
  Tag, User, Users, BarChart3, Lightbulb, FileText, Film, Share2,
  Workflow, Layers, Wallet, Zap, TrendingUp,
} from 'lucide-react';

export const APP_URL = 'https://app.loopai.in';
export const goToApp = () => { window.location.href = APP_URL; };
export const whatsappUrl = (msg) => `https://wa.me/919741892299?text=${encodeURIComponent(msg)}`;
export const WHATSAPP_URL = whatsappUrl('Hi LoopAI, I need help with ');

const IMG = 'https://static.prod-images.emergentagent.com/jobs/46d26749-4933-45ec-8b1e-eb0725ada51e/images/';
export const IMAGES = {
  hero: `${IMG}8928b8a495a2ba6a238e2e48f249f32a09f4ce06b5e9f587006e7ced21564d4a.jpeg`,
  heroProduct: `${IMG}106a665831b22c873d1272f1f5a885b9a93d67ebfc77f128733ac0a111c1cc73.jpeg`,
  cta: `${IMG}33621d8b4645183850bd3f3c621414b13dfa375d51af5b1da852ee6a7a5d58f7.jpeg`,
  sneaker: `${IMG}4953aef2c0e9e3028ff88235385931dffe411b957fdcc587590ab08d5612c10b.jpeg`,
  productAd: `${IMG}820ab86737ae6c81fba3166320273eee0b5b93c2dd89f7fc835ffa8852ff002f.jpeg`,
  social: `${IMG}0246d995ffce437fe5e05e0fa5bedadab4c7c89e9995a308a07da95d9f3cd0ea.jpeg`,
  cinematic: `${IMG}edb62129179835d79adc61edcbae13ab29664ede3e0286258efffee437bbfa6e.jpeg`,
  avatar: `${IMG}5e91f244495eda14aec401da22e84f9f4ec5dc937a4427f61a1890f3a5dc6b0e.jpeg`,
  aiImage: `${IMG}5a8313af22d4ef0224057ba8e393bab29ede547d95a8d35c1ab8a3745596bee5.jpeg`,
  campaign: `${IMG}3fc080d15e037e4b254f89ac47d18a06152ed21dc6ab349c538c9a7c22aa2703.jpeg`,
  creators: `${IMG}5386f9db4964a41bb9cc7ab3e68c994a78a89b5702d57a8e51a30a2a8c003f1a.jpeg`,
  agencies: `${IMG}758562a249994b05238a7e9d20d29ce87d3daeaf732c52918672f8b538b85e56.jpeg`,
  marketing: `${IMG}fa03735f0cacdf6bf47f6b61ef0c548dcaf067971fab803d853ac681f92fcaf2.jpeg`,
  videoAd: `${IMG}e485c23fbed6344df38d0bf84c05a479df8318830382544cc7ab8bf38d5794d7.jpeg`,
  imageGen: `${IMG}a82c18b71afc9d944667cc3c034c6d248f4be6fd1813720f0ad98a95bed61293.jpeg`,
  studioPro: `${IMG}44ac286b76e4cfe8e35ee2705dc7a3001e10cb1a8450794b82e4e33dff3b5112.jpeg`,
  movieStudio: `${IMG}f5ddc85dff8ebb956e651554b4306b01169b3c6e5b49dd5506bc595600418155.jpeg`,
  brandAssets: `${IMG}3a5ae6dfefe60a90e6a9914b24aff73f3407ee3fb69ea86fdf99cec62dd77939.jpeg`,
};

export const NAV_LINKS = [
  { name: 'Product', href: '#tools' },
  { name: 'Use Cases', href: '#use-cases' },
  { name: 'Examples', href: '#examples' },
  { name: 'Pricing', href: '#pricing' },
  { name: 'How it works', href: '#how-it-works' },
  { name: 'Why LoopAI', href: '#why' },
];

export const HERO_TOOLS = [
  { title: 'Ad Studio', desc: 'Turn products into ads', icon: Tag },
  { title: 'Studio Pro', desc: 'Advanced creative control', icon: SlidersHorizontal },
  { title: 'Movie Studio', desc: 'Cinematic storytelling', icon: Clapperboard },
  { title: 'Image Studio', desc: 'Stunning visuals', icon: ImageIcon },
  { title: 'Content Planner', desc: 'Plan and publish', icon: CalendarDays },
];

export const EXAMPLE_FILTERS = ['All', 'Product Ads', 'Social Media', 'Cinematic Films', 'AI Avatars', 'AI Images', 'Campaigns'];

export const EXAMPLES = [
  { title: 'Product Ad', desc: 'Turn any product into high-converting ads', category: 'Product Ads', image: IMAGES.productAd },
  { title: 'Social Media Video', desc: 'Create reels, shorts and UGC content', category: 'Social Media', image: IMAGES.social },
  { title: 'Cinematic Film', desc: 'Bring your stories to life', category: 'Cinematic Films', image: IMAGES.cinematic },
  { title: 'AI Avatar', desc: 'Lifelike presenters and brand avatars', category: 'AI Avatars', image: IMAGES.avatar },
  { title: 'AI Image', desc: 'Stunning visuals for any purpose', category: 'AI Images', image: IMAGES.aiImage },
  { title: 'Campaign', desc: 'Plan, create and publish content at scale', category: 'Campaigns', image: IMAGES.campaign },
];

export const GOALS = [
  { title: 'Brands & D2C', desc: 'Product videos, ads and campaigns that drive sales.', icon: Tag, color: 'from-violet-500 to-fuchsia-500', image: IMAGES.heroProduct },
  { title: 'Creators & Influencers', desc: 'Create short-form content that stands out.', icon: User, color: 'from-cyan-500 to-sky-500', image: IMAGES.creators },
  { title: 'Agencies', desc: 'Streamline production for multiple clients.', icon: Users, color: 'from-purple-500 to-indigo-500', image: IMAGES.agencies },
  { title: 'Marketing Teams', desc: 'Plan, create and publish content at scale.', icon: BarChart3, color: 'from-blue-500 to-indigo-500', image: IMAGES.marketing },
];

export const CORE_TOOLS = [
  { id: 1, apiKey: 'video_creator', title: 'AI Video & Ad Creator', description: 'Create product ads, social videos and promotional content.', icon: Video, image: IMAGES.videoAd },
  { id: 2, apiKey: 'image_creator', title: 'AI Image Generator', description: 'Stunning branded visuals and campaign assets.', icon: ImageIcon, image: IMAGES.imageGen },
  { id: 3, title: 'Studio Pro', description: 'Advanced creative control with camera, shots and editing.', icon: SlidersHorizontal, image: IMAGES.studioPro },
  { id: 4, apiKey: 'movie_maker', title: 'Movie Studio', description: 'Multi-scene storytelling for cinematic production.', icon: Clapperboard, image: IMAGES.movieStudio },
  { id: 5, title: 'Content Planner', description: 'Plan, schedule and publish across multiple channels.', icon: CalendarDays, image: IMAGES.campaign },
  { id: 6, title: 'Brand & Asset Management', description: 'Organize brands, products and reusable assets.', icon: FolderKanban, image: IMAGES.brandAssets },
];

export const STEPS = [
  { no: '01', title: 'Start', desc: 'Enter a product URL, upload an image, or describe your idea.', icon: Lightbulb },
  { no: '02', title: 'Plan', desc: 'Get AI-powered concepts, scripts and storyboards.', icon: FileText },
  { no: '03', title: 'Create & Refine', desc: 'Generate videos, images and edit with creative control.', icon: Film },
  { no: '04', title: 'Deliver', desc: 'Export and publish to your preferred channels.', icon: Share2 },
];

export const WHY_FEATURES = [
  { title: 'Connected creative workflow', desc: 'From idea to final content without switching tools.', icon: Workflow },
  { title: 'Professional creative control', desc: 'Guide scenes, shots, characters and styles.', icon: SlidersHorizontal },
  { title: 'Multiple brands & assets', desc: 'Store and reuse brand info, products and assets.', icon: Layers },
  { title: 'Flexible pricing', desc: 'Pay only for what you create. No long subscriptions.', icon: Wallet },
  { title: 'Access to leading AI models', desc: 'Use the best AI models through one platform.', icon: Zap },
  { title: 'Built for real results', desc: 'Designed for marketing and business outcomes.', icon: TrendingUp },
];

export const PRICING_PLANS = [
  {
    name: 'Pay As You Go',
    price: '₹100',
    credits: '100 Credits',
    desc: 'Minimum top-up',
    features: ['Access Image & Video generation tools', '1 Credit = ₹1', 'No Expiration'],
    highlighted: false,
    savings: null,
  },
  {
    name: 'Starter Pack',
    price: '₹2,700',
    credits: '3,000 Credits',
    desc: 'For hobbyists',
    features: ['Access Image, Video & Avatar tools', 'Save 10% on credits', 'Generates ~150 Videos'],
    highlighted: false,
    savings: '10% OFF',
  },
  {
    name: 'Creator Pack',
    price: '₹8,500 / mo',
    credits: '10,000 Credits',
    desc: 'Monthly Subscription',
    features: ['Access All Tools + Content Planner', 'Save 15% on credits', 'Priority Generation'],
    highlighted: true,
    savings: '15% OFF',
  },
  {
    name: 'Studio Pack',
    price: '₹20,000 / mo',
    credits: '25,000 Credits',
    desc: 'Monthly Subscription',
    features: ['Access All Tools', 'Advanced Editor (Edit each shot)', 'Save 20% on credits', 'Dedicated Manager'],
    highlighted: false,
    savings: '20% OFF',
  },
];
